import useLanguage from '@op/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Checkbox } from 'tea-component';

/** 隐藏评论的 CSS 类名常量 */
export const HIDE_COMMENTS_CLASS = 'dbo-hide-comments';

/** 评论元素的 CSS 选择器 */
export const COMMENTS_SELECTOR = '.dbo-md-comments-ul-list';

/** localStorage 存储 key 常量 */
export const COMMENTS_TOGGLE_STORAGE_KEY = 'dbo-gpt-advanced-comments-toggle';

interface CommentsToggleProps {
  /** 是否勾选（显示评论），默认 true */
  checked?: boolean;
  /** 状态变更回调 */
  onChange?: (checked: boolean) => void;
  /** 自定义 label 文本，未传则使用默认值 */
  label?: string;
  /** 国际化方法 */
  formatMessage?: (msg: { id: string }) => string;
  /** 自定义类名 */
  className?: string;
  /** 是否禁用，默认 false */
  disabled?: boolean;
}

/**
 * 评论内容显示/隐藏切换组件
 * 用于控制 GPT 高级报告中评论内容的显示与隐藏
 */
export function CommentsToggle({
  checked = true,
  onChange,
  label,
  className = '',
  disabled = false,
}: CommentsToggleProps) {
  const { formatMessage } = useLanguage(); 

  return (
    <label className={`dbo-ai-comments-toggle ${className}`.trim()}>
      <Checkbox
        value={checked}
        onChange={(value) => {
          onChange?.(value);
        }}
        disabled={disabled}
      >
        {label || formatMessage({ id: 'Show Representative Reviews' })}
      </Checkbox>
    </label>
  );
}

interface UseCommentsToggleOptions {
  /** 初始值，默认 true */
  defaultValue?: boolean;
  /** 是否持久化到 localStorage */
  persist?: boolean;
  /** localStorage key，默认 'dbo-gpt-advanced-comments-toggle' */
  storageKey?: string;
  /** 状态变更时的额外回调 */
  onToggle?: (checked: boolean) => void;
}

interface UseCommentsToggleResult {
  /** 是否显示评论 */
  showComments: boolean;
  /** 设置显示状态 */
  setShowComments: (checked: boolean) => void;
  /** 是否检测到评论元素 */
  hasComments: boolean;
  /** 设置检测结果 */
  setHasComments: (hasComments: boolean) => void;
  /** 切换显示状态 */
  toggleComments: () => void;
  /** 隐藏评论的 CSS 类名 */
  hideCommentsClassName: string;
  /** 渲染 CommentsToggle 组件的便捷方法 */
  renderToggle: (props?: Omit<CommentsToggleProps, 'checked' | 'onChange'>) => React.ReactNode;
}

/**
 * 管理评论显示/隐藏状态的 Hook
 * @param options 配置选项
 * @returns 状态和渲染方法
 */
export function useCommentsToggle(
  options: UseCommentsToggleOptions = {}
): UseCommentsToggleResult {
  const {
    defaultValue = false,
    persist = false,
    storageKey = COMMENTS_TOGGLE_STORAGE_KEY,
    onToggle,
  } = options;

  // 初始化状态：如果开启持久化，从 localStorage 读取
  const [showComments, setShowComments] = useState<boolean>(() => {
    if (persist) {
      const stored = localStorage.getItem(storageKey);
      return stored === null ? defaultValue : stored !== 'false';
    }
    return defaultValue;
  });

  // 是否检测到评论元素
  const [hasComments, setHasComments] = useState(false);

  // 持久化到 localStorage
  useEffect(() => {
    if (persist) {
      localStorage.setItem(storageKey, String(showComments));
    }
  }, [showComments, persist, storageKey]);

  // 切换显示状态
  const toggleComments = () => {
    const newValue = !showComments;
    setShowComments(newValue);
    onToggle?.(newValue);
  };

  // 设置状态并触发回调
  const handleSetShowComments = (checked: boolean) => {
    setShowComments(checked);
    onToggle?.(checked);
  };

  // 隐藏评论的 CSS 类名
  const hideCommentsClassName = showComments ? '' : HIDE_COMMENTS_CLASS;

  // 便捷渲染方法
  const renderToggle = (
    props?: Omit<CommentsToggleProps, 'checked' | 'onChange'>
  ) => (
    <CommentsToggle
      checked={showComments}
      onChange={handleSetShowComments}
      {...props}
    />
  );

  return {
    showComments,
    setShowComments: handleSetShowComments,
    hasComments,
    setHasComments,
    toggleComments,
    hideCommentsClassName,
    renderToggle,
  };
}

/**
 * 简化版 Hook：仅用于简单场景
 * @param defaultValue 默认值
 * @returns 状态和类名
 */
export function useCommentsToggleSimple(defaultValue = true) {
  const [showComments, setShowComments] = useState(defaultValue);

  return {
    showComments,
    setShowComments,
    hideClassName: showComments ? '' : HIDE_COMMENTS_CLASS,
  };
}
